/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { BLOCKS } from '@contentful/rich-text-types';

import { Grid, Typography } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { InstructorProfile } from 'bundles/degree-description/__generated__/baseContentfulTypes';
import type { DdpFieldsBelowFoldFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import Instructor from 'bundles/degree-description/components/modules/instructor-section/Instructor';
import Container from 'bundles/degree-description/components/shared/Container';
import { filterRichTextContentByNodeType } from 'bundles/degree-description/components/shared/RichText';
import { useDegreeDescriptionPageTheme } from 'bundles/degree-description/degreeDescriptionPageTheme';

type Props = {
  title: string;
  json?: NonNullable<DdpFieldsBelowFoldFragment['instructorsSection']>['json'];
  instructorItems?: InstructorProfile[];
};

const styles = {
  instructorSection: () => css`
    padding: var(--cds-spacing-600) 0;
  `,
  title: (theme: Theme) => css`
    &.cds-grid-item {
      padding-top: 0;
    }

    h3 {
      ${theme.typography.h1semibold}
      b, strong {
        ${theme.typography.h1semibold}
      }
    }
  `,
};

const InstructorSectionModule = ({ instructorItems, title, json }: Props) => {
  const theme = useDegreeDescriptionPageTheme();

  const titleContent = filterRichTextContentByNodeType(json, [BLOCKS.HEADING_3]);

  // Some programs might not have the title set.
  // If they do, use the first header's text.
  // If not, use the default string from `title`
  const firstHeaderNodeType = titleContent?.content?.[0]?.content?.[0]?.nodeType;
  const firstHeaderText = firstHeaderNodeType === 'text' ? titleContent?.content?.[0]?.content?.[0]?.value : null;

  return (
    <div css={styles.instructorSection}>
      <Container>
        <Grid container spacing={32}>
          <Grid item xs={12} css={styles.title}>
            {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
            <Typography component="h3" variant="h1semibold" css={theme.mobileFontSizeStyle?.header}>
              {/* Prevents rendering a blank string */}
              {/* eslint-disable-next-line no-extra-boolean-cast */}
              {!!firstHeaderText ? firstHeaderText : title}
            </Typography>
          </Grid>
          {instructorItems?.map((item) => {
            return <Instructor key={`instructor_${item.sys.id}`} instructorItem={item} />;
          })}
        </Grid>
      </Container>
    </div>
  );
};

export default InstructorSectionModule;
